import { Product, SaleRow } from "@/models/base";
import { ProductService, VatService } from "@/services";
import { i18n } from "@/setup/i18n-setup";
import { getEnumKeys, getEnumValueFromKey } from "../enum";
import { SaleEditState } from "./sale-edit-state";

const MAX_ROWS = 5000;

export enum AllowedCols{
	Qty = "aantal",
	Sku = "artnr",
	BarCode = "barcode",
	SkuOrBarCode = "artnr-of-barcode",
	Description = "omschrijving",
	UnitPrice = "eenheidsprijs",
	Discount = "korting"
}

export interface ICsvToSaleRowsOptions {
	useCommaAsDeciamlSeperator?:boolean;
}

export function allowedColsAsList():string[]{
	return getEnumKeys(AllowedCols).map(k=>{
		return getEnumValueFromKey(AllowedCols, k);
	});
}

function createError(key:string, data?:{[key:string]:any}):Error{
	return new Error(i18n.t(`components.sales.rows-csv-errors.${key}`, data).toString());
}

export async function csvToSaleRows(csvFile:File, state:SaleEditState, options:ICsvToSaleRowsOptions = {}):Promise<SaleRow[]>{
	let reader = new FileReader();
	let result:string = await new Promise((resolve, reject)=>{
		reader.addEventListener("load", ()=>{
			resolve(reader.result as string);
		});
		reader.addEventListener("error", (err)=>{
			console.error(err);
			reject(createError("failed-to-read-file"));
		});
		reader.readAsText(csvFile);
	});
	if (!result) {
		throw createError("file-is-empty");
	}
	let lines = result.split(/(?<!\\)\n/);
	let header = lines.shift() || "";
	let cols:AllowedCols[] = splitLine(header.toLowerCase()) as AllowedCols[];
	let splittedLines = splitLines(lines, cols);
	if (lines.length > MAX_ROWS){
		throw createError("file-is-to-large", {max: MAX_ROWS});
	}

	let allowedColsList = allowedColsAsList();
	for (let col of cols) {
		if (allowedColsList.indexOf(col) == -1) {
			throw createError(`invalid-col`, {col});
		}
	}
	let products = await fetchProducts(splittedLines, cols);
	let rows:SaleRow[] = [];
	for (let line of splittedLines) {
		if (line.length == 0) continue;
		rows.push(lineToSaleRow(state, line, cols, products, options));
	}
	return rows;
}

async function fetchProductsByUniqueSkus(lines:string[][], cols:AllowedCols[]):Promise<Product[]> {
	let skuIndex = -1;
	for (let i = 0; i < cols.length;i++){
		if (cols[i] == AllowedCols.Sku){
			skuIndex = i;
			break;
		}
	}
	if (skuIndex == -1) return [];
	let skus:string[] = [];

	for (let line of lines) {
		let sku = line[skuIndex];
		if (!sku) continue;
		sku = sku.trim();
		if (skus.indexOf(sku) != -1) continue;
		skus.push(sku);
	}

	if (skus.length == 0) return [];
	let products = await ProductService.getProductsBySkus(skus, {batchSize: 100, fullPreloads: true});
	if (products.length < skus.length) {
		let notFoundSkus = skus.filter(s=>{
			return products.find(p=>p.Sku == s) == null;
		});
		throw createError("skus-not-found", {skus: notFoundSkus.join(", ")});
	}

	return products;
}

async function fetchProductsByUniqueBarCodes(lines:string[][], cols:AllowedCols[]):Promise<Product[]> {
	let barcodeIndex = -1;
	for (let i = 0; i < cols.length;i++){
		if (cols[i] == AllowedCols.BarCode){
			barcodeIndex = i;
			break;
		}
	}
	if (barcodeIndex == -1) return [];
	let barcodes:string[] = [];

	for (let line of lines) {
		let barcode = line[barcodeIndex];
		if (!barcode) continue;
		barcode = barcode.trim();
		if (barcodes.indexOf(barcode) != -1) continue;
		barcodes.push(barcode);
	}

	if (barcodes.length == 0) return [];
	let products = await ProductService.getProductsByBarcodes(barcodes, {batchSize: 100, fullPreloads: true});
	if (products.length < barcodes.length) {
		let barcodesNotFound = barcodes.filter(s=>{
			return products.find(p=>p.BarCode == s) == null;
		});
		throw createError("skus-not-found", {skus: barcodesNotFound.join(", ")});
	}

	return products;
}

async function fetchProductsByUniqueSkusOrBarCodes(lines:string[][], cols:AllowedCols[]):Promise<Product[]> {
	let skuIndex = -1;
	for (let i = 0; i < cols.length;i++){
		if (cols[i] == AllowedCols.SkuOrBarCode){
			skuIndex = i;
			break;
		}
	}
	if (skuIndex == -1) return [];
	let skus:string[] = [];

	for (let line of lines) {
		let sku = line[skuIndex];
		if (!sku) continue;
		sku = sku.trim();
		if (skus.indexOf(sku) != -1) continue;
		skus.push(sku);
	}
	if (skus.length == 0) return [];
	let products = await ProductService.GetProductsBySkusOrBarCodes(skus, {batchSize: 100, fullPreloads: true});
	if (products.length < skus.length) {
		let notFoundSkus = skus.filter(s=>{
			return products.find(p=>p.Sku == s || p.BarCode == s) == null;
		});
		throw createError("skus-not-found", {skus: notFoundSkus.join(", ")});
	}

	return products;

}

async function fetchProducts(lines:string[][], cols:AllowedCols[]):Promise<Product[]>{
	let skus = await fetchProductsByUniqueSkus(lines, cols);
	let barcodes = await fetchProductsByUniqueBarCodes(lines, cols);
	let skusOrBarcodes = await fetchProductsByUniqueSkusOrBarCodes(lines, cols);

	let result = skus.concat(barcodes).concat(skusOrBarcodes);
	for (let i = result.length -1; i >=0;i--){
		let product = result[i];
		let foundIndex = result.findIndex(p=>product.ID == p.ID);
		if (foundIndex == i) continue;
		result.splice(i,1);
	}
	return result;
}

function getFloatValue(colValue:string, options:ICsvToSaleRowsOptions):number {
	colValue =  colValue.replaceAll(" ", "");
	if (options.useCommaAsDeciamlSeperator) {
		colValue = colValue.replaceAll(" ", "").replaceAll(".", "").replaceAll(",", ".");
	}
	let value = parseFloat(colValue);
	if (Number.isNaN(value)) {
		value = 0;
	}
	return value;
}


// eslint-disable-next-line max-lines-per-function
function lineToSaleRow(state:SaleEditState, line:string[], cols:AllowedCols[], products:Product[], options:ICsvToSaleRowsOptions):SaleRow{
	let row = new SaleRow();
	let skuIndex = cols.indexOf(AllowedCols.Sku);
	let barcodeIndex = cols.indexOf(AllowedCols.BarCode);
	let skuOrBarCodeIndex = cols.indexOf(AllowedCols.SkuOrBarCode);
	let product:Product | undefined = undefined;
	if (skuIndex != -1){
		product = products.find(p=>p.Sku == line[skuIndex]);
	}else if (barcodeIndex != -1){
		product = products.find(p=>p.BarCode == line[barcodeIndex]);
	}else if (skuOrBarCodeIndex != -1){
		product = products.find(p=>p.Sku == line[skuOrBarCodeIndex] || p.BarCode == line[skuOrBarCodeIndex]);
	}
	if (product){
		row.ProductSku = product.Sku || "";
		row.Product = product;
		row.ProductDescription = product.getName("nl-be");
		row.UnitPrice = (row.Product[`SellPrice${state.sale.PriceCategory || 0}` as keyof Product] as number) || row.Product.SellPrice1;
		row.UnitBuyPrice = row.Product.BuyNettoPrice || 0;
		if (state.sale.Contact){
			if (state.sale.Contact.MainAddress) {
				row.ProductDescription = product.getName(state.sale.Contact.MainAddress.Lng);
			}
			if (!product.DiscountsDisabled) {
				row.Discount = state.sale.Contact.Discount;
			}
		}
		if (state.sale.VatRegime != 1) {
			row.Vat = VatService.getVatRegime(state.sale.VatRegime).getSaleVat();
		}else if (product.Vat) {
			row.Vat = product.Vat.Value;
		}
	}
	for (let i = 0; i < cols.length; i++) {
		let lineValue = line[i];
		if (!lineValue) continue;
		if (cols[i] == AllowedCols.Qty) {
			row.setAmount(getFloatValue(lineValue, options));
		}else if (cols[i] == AllowedCols.Description) {
			row.ProductDescription = lineValue;
		}else if (cols[i] == AllowedCols.UnitPrice) {
			row.UnitPrice = getFloatValue(lineValue, options);
		}else if (cols[i] == AllowedCols.Discount){
			row.Discount = getFloatValue(lineValue, options);
		}
	}
	if (cols.indexOf(AllowedCols.UnitPrice) == -1){
		row.UnitPrice = state.computeRowUnitPrice(row);
	}
	return row;
}

function splitLine(line:string):string[]{
	return line.split(/(?<!\\);/).map(l=>l.trim());
}

function splitLines(lines:string[], headers:string[]):string[][]{
	return lines.map((l, i)=>{
		l = l.trim();
		if (!l) return [];
		let line = splitLine(l);
		if (line.length != 0 && line.length != headers.length) {
			return line;
		}
		if (line.length != headers.length){
			throw createError("line-is-not-valid", {line: i+2});
		}
		return line;
	});
}
